import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from 'react'
import type { TerraDraw } from 'terra-draw'
import type { MapInteractionMode, DrawShape, UndoEntry } from '@/types/draw'

// =============================================================================
// State
// =============================================================================

interface DrawState {
  activeMode: MapInteractionMode
  toggledShape: DrawShape
  shapeMenuOpen: boolean
  isDrawingInProgress: boolean
  undoStack: UndoEntry[]
  selectedFeatureIds: Set<string>
}

const initialState: DrawState = {
  activeMode: 'pan-select',
  toggledShape: 'polygon',
  shapeMenuOpen: false,
  isDrawingInProgress: false,
  undoStack: [],
  selectedFeatureIds: new Set(),
}

// =============================================================================
// Actions
// =============================================================================

type DrawAction =
  | { type: 'SET_MODE'; payload: MapInteractionMode }
  | { type: 'SET_TOGGLED_SHAPE'; payload: DrawShape }
  | { type: 'SET_SHAPE_MENU_OPEN'; payload: boolean }
  | { type: 'TOGGLE_SHAPE_MENU' }
  | { type: 'SET_DRAWING_IN_PROGRESS'; payload: boolean }
  | { type: 'PUSH_UNDO'; payload: UndoEntry }
  | { type: 'POP_UNDO' }
  | { type: 'CLEAR_UNDO' }
  | { type: 'SELECT_FEATURE'; payload: string }
  | { type: 'TOGGLE_FEATURE_SELECTION'; payload: string }
  | { type: 'CLEAR_SELECTION' }
  | { type: 'DEACTIVATE_TOOL' }

function drawReducer(state: DrawState, action: DrawAction): DrawState {
  switch (action.type) {
    case 'SET_MODE':
      return {
        ...state,
        activeMode: action.payload,
        shapeMenuOpen: false,
      }
    case 'SET_TOGGLED_SHAPE':
      return { ...state, toggledShape: action.payload }
    case 'SET_SHAPE_MENU_OPEN':
      return { ...state, shapeMenuOpen: action.payload }
    case 'TOGGLE_SHAPE_MENU':
      return { ...state, shapeMenuOpen: !state.shapeMenuOpen }
    case 'SET_DRAWING_IN_PROGRESS':
      return { ...state, isDrawingInProgress: action.payload }
    case 'PUSH_UNDO':
      return { ...state, undoStack: [...state.undoStack, action.payload] }
    case 'POP_UNDO':
      return { ...state, undoStack: state.undoStack.slice(0, -1) }
    case 'CLEAR_UNDO':
      return { ...state, undoStack: [] }
    case 'SELECT_FEATURE': {
      const next = new Set<string>()
      next.add(action.payload)
      return { ...state, selectedFeatureIds: next }
    }
    case 'TOGGLE_FEATURE_SELECTION': {
      const next = new Set(state.selectedFeatureIds)
      if (next.has(action.payload)) {
        next.delete(action.payload)
      } else {
        next.add(action.payload)
      }
      return { ...state, selectedFeatureIds: next }
    }
    case 'CLEAR_SELECTION':
      return {
        ...state,
        selectedFeatureIds:
          state.selectedFeatureIds.size === 0
            ? state.selectedFeatureIds
            : new Set(),
      }
    case 'DEACTIVATE_TOOL':
      return {
        ...state,
        activeMode: 'pan-select',
        isDrawingInProgress: false,
        shapeMenuOpen: false,
        undoStack: [],
        selectedFeatureIds:
          state.selectedFeatureIds.size === 0
            ? state.selectedFeatureIds
            : new Set(),
      }
    default:
      return state
  }
}

// =============================================================================
// Context
// =============================================================================

interface DrawContextValue {
  activeMode: MapInteractionMode
  toggledShape: DrawShape
  shapeMenuOpen: boolean
  isDrawingInProgress: boolean
  undoStack: UndoEntry[]
  selectedFeatureIds: Set<string>
  /** Terra Draw instance when the map bridge is mounted (for keyboard shortcuts). */
  terraDrawRef: React.MutableRefObject<TerraDraw | null>
  /**
   * One-shot `mamid` of a smart-layer draft to auto-select once the smart-layer
   * edit-geometry mode is active. Set by the map's double-click handler right
   * before `setActiveMode('edit-geometry')`; consumed (and cleared) by the
   * Terra Draw sync hook after it resyncs select mode from the smart store.
   */
  pendingSmartEditSelectionRef: React.MutableRefObject<string | null>

  setActiveMode: (mode: MapInteractionMode) => void
  setToggledShape: (shape: DrawShape) => void
  activateShape: (shape: DrawShape) => void
  setShapeMenuOpen: (open: boolean) => void
  toggleShapeMenu: () => void
  setDrawingInProgress: (inProgress: boolean) => void
  pushUndo: (entry: UndoEntry) => void
  popUndo: () => UndoEntry | undefined
  clearUndo: () => void
  selectFeature: (featureId: string) => void
  toggleFeatureSelection: (featureId: string) => void
  clearSelection: () => void
  deactivateTool: () => void
}

const DrawContext = createContext<DrawContextValue | null>(null)

// =============================================================================
// Provider
// =============================================================================

export const DrawProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(drawReducer, initialState)
  const terraDrawRef = useRef<TerraDraw | null>(null)
  const pendingSmartEditSelectionRef = useRef<string | null>(null)

  const setActiveMode = useCallback((mode: MapInteractionMode) => {
    dispatch({ type: 'SET_MODE', payload: mode })
  }, [])

  const setToggledShape = useCallback((shape: DrawShape) => {
    dispatch({ type: 'SET_TOGGLED_SHAPE', payload: shape })
  }, [])

  const activateShape = useCallback((shape: DrawShape) => {
    const modeMap: Record<DrawShape, MapInteractionMode> = {
      polygon: 'draw-polygon',
      line: 'draw-line',
      point: 'draw-point',
      rectangle: 'draw-rectangle',
      ellipse: 'draw-ellipse',
    }
    dispatch({ type: 'SET_TOGGLED_SHAPE', payload: shape })
    dispatch({ type: 'SET_MODE', payload: modeMap[shape] })
  }, [])

  const setShapeMenuOpen = useCallback((open: boolean) => {
    dispatch({ type: 'SET_SHAPE_MENU_OPEN', payload: open })
  }, [])

  const toggleShapeMenu = useCallback(() => {
    dispatch({ type: 'TOGGLE_SHAPE_MENU' })
  }, [])

  const setDrawingInProgress = useCallback((inProgress: boolean) => {
    dispatch({ type: 'SET_DRAWING_IN_PROGRESS', payload: inProgress })
  }, [])

  const pushUndo = useCallback((entry: UndoEntry) => {
    dispatch({ type: 'PUSH_UNDO', payload: entry })
  }, [])

  const popUndo = useCallback((): UndoEntry | undefined => {
    const last = state.undoStack[state.undoStack.length - 1]
    if (last) {
      dispatch({ type: 'POP_UNDO' })
    }
    return last
  }, [state.undoStack])

  const clearUndo = useCallback(() => {
    dispatch({ type: 'CLEAR_UNDO' })
  }, [])

  const selectFeature = useCallback((featureId: string) => {
    dispatch({ type: 'SELECT_FEATURE', payload: featureId })
  }, [])

  const toggleFeatureSelection = useCallback((featureId: string) => {
    dispatch({ type: 'TOGGLE_FEATURE_SELECTION', payload: featureId })
  }, [])

  const clearSelection = useCallback(() => {
    dispatch({ type: 'CLEAR_SELECTION' })
  }, [])

  const deactivateTool = useCallback(() => {
    dispatch({ type: 'DEACTIVATE_TOOL' })
  }, [])

  const value = useMemo<DrawContextValue>(
    () => ({
      activeMode: state.activeMode,
      toggledShape: state.toggledShape,
      shapeMenuOpen: state.shapeMenuOpen,
      isDrawingInProgress: state.isDrawingInProgress,
      undoStack: state.undoStack,
      selectedFeatureIds: state.selectedFeatureIds,
      terraDrawRef,
      pendingSmartEditSelectionRef,
      setActiveMode,
      setToggledShape,
      activateShape,
      setShapeMenuOpen,
      toggleShapeMenu,
      setDrawingInProgress,
      pushUndo,
      popUndo,
      clearUndo,
      selectFeature,
      toggleFeatureSelection,
      clearSelection,
      deactivateTool,
    }),
    [
      state.activeMode,
      state.toggledShape,
      state.shapeMenuOpen,
      state.isDrawingInProgress,
      state.undoStack,
      state.selectedFeatureIds,
      terraDrawRef,
      pendingSmartEditSelectionRef,
      setActiveMode,
      setToggledShape,
      activateShape,
      setShapeMenuOpen,
      toggleShapeMenu,
      setDrawingInProgress,
      pushUndo,
      popUndo,
      clearUndo,
      selectFeature,
      toggleFeatureSelection,
      clearSelection,
      deactivateTool,
    ]
  )

  return <DrawContext.Provider value={value}>{children}</DrawContext.Provider>
}

// =============================================================================
// Hook
// =============================================================================

export const useDrawContext = (): DrawContextValue => {
  const context = useContext(DrawContext)
  if (!context) {
    throw new Error('useDrawContext must be used within a DrawProvider')
  }
  return context
}
